import { RouteComponentProps, StaticContext, withRouter } from 'react-router';
import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import NavigationStatus from '../components/NavigationStatus';
import TrustPilotReviewCollector from '../components/TrustPilotReviewCollector';
import {
  BoxData,
  CheckoutItem,
  CheckoutProduct,
  Dimensions,
  Discount,
  Product,
} from '../models/APIModels';
import { Address } from '../components/AddressLookup';
import {
  orderCostToMoney,
  purchaseCostToMoney,
  suitcaseOrderToMoney,
} from '../utilities/MoneyConversion';
import useGtmEventTracking from '../components/useGtmEventTracking';

interface PaymentProps
  extends RouteComponentProps<undefined, StaticContext, PaymentState> {}

interface PaymentState {
  boxes: BoxData[];
  collectionAddress: Address;
  collectionDate: Date;
  deliveryAddress?: Address;
  dontKnowAddress: boolean;
  deliveryDate?: Date;
  dontKnowDate: boolean;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  eecProducts?: CheckoutProduct[];
  items?: CheckoutItem[];
  orderId?: number;
  discount?: Discount;
  // Removals specific fields
  removalService?: boolean;
  selectedTier?: any;
  questions?: any;
  moveDate?: Date;
  selectedPacking?: any;
  selectedStorage?: any;
  wantsPacking?: boolean;
  wantsStorage?: boolean;
}

function SuccessComponent({ history }: PaymentProps) {
  const {
    boxes,
    suitcases,
    storageRequired,
    suitcaseData,
    eecProducts,
    items,
    orderId,
    discount,
    removalService,
    selectedTier,
  } = history.location.state || {};
  
  // Confetti state
  const [showConfetti, setShowConfetti] = useState(true);
  
  // Determine if this is a removals order
  const isRemovalsOrder = removalService && selectedTier;
  
  const cost = isRemovalsOrder
    ? '£0.00' // Removals cost is not needed for display
    : suitcases
    ? suitcaseOrderToMoney(
        suitcases.length,
        suitcaseData?.shippingCost,
        discount,
      )
    : storageRequired
    ? purchaseCostToMoney(boxes, discount, true)
    : orderCostToMoney(boxes, true, discount);


  const deliveryFeeString = process.env.REACT_APP_DELIVERY_FEE;
  if (!deliveryFeeString) {
    throw new Error('No delivery fee set');
  }
  const deliveryFee = parseInt(deliveryFeeString);

  let revenue = isRemovalsOrder ? '0' : cost.substring(1);
  const eecPurchaseObject = {
    event: 'eec_purchase',
    ecommerce: {
      currencyCode: 'GBP',
      purchase: {
        actionField: {
          id: orderId,
          revenue: revenue,
          shipping: suitcases ? cost : deliveryFee / 100,
          affiliation: 'Online Store',
          coupon: discount?.discountCode,
        },
        products: eecProducts,
      },
    },
  };

  const purchaseObject = {
    event: 'purchase',
    ecommerce: {
      transaction_id: orderId,
      affiliation: 'Online Store',
      value: Number.parseFloat(revenue),
      shipping: suitcases ? cost : deliveryFee / 100,
      currency: 'GBP',
      coupon: discount?.discountCode,
    },
    items: items,
  };

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: 'booking_step_purchase' });
    gtmEventTracking(eecPurchaseObject);
    gtmEventTracking(purchaseObject);

    // Meta Pixel: Track Purchase
    const purchaseValue = Number.parseFloat(revenue);
    window.fbq('track', 'Purchase', {
      value: purchaseValue,
      currency: 'GBP',
      content_name: 'Single_Vac',
      content_type: 'product',
      num_items: items?.length || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtmEventTracking]);

  // CRITICAL: Clear all payment state markers after successful order
  useEffect(() => {
    // Clear payment state from localStorage (order is complete)
    localStorage.removeItem('payment-state');
    // Clear sessionStorage markers - user can now create new orders
    sessionStorage.removeItem('order-created');
    sessionStorage.removeItem('created-order-id');
    sessionStorage.removeItem('order-collection-address');
    sessionStorage.removeItem('order-collection-date');
  }, []);

  // Stop confetti after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {showConfetti && <Confetti recycle={true} numberOfPieces={500} />}
      {!isRemovalsOrder && <NavigationStatus step={4} />}
      <div className="App-content">
        <div className="success">
          <h1 className="kkUltraViolet-h2">Success!</h1>
          <h2>Thank you for your order</h2>
          <span>Check your email for an order confirmation</span>
          {/*<span>Stay in the know, track your order in <Link to="/account">My account</Link></span>*/}

          {/* Survey Button */}
          <div className="survey-section">
            <button
              className="survey-button"
              onClick={() =>
                window.open(
                  'https://docs.google.com/forms/d/e/1FAIpQLSd4esHndYuAAMpI8SQIGiZB1GqwEivNKepBaSKpOVoOPWX14A/viewform',
                  '_blank',
                )
              }
            >
              Win a £100 Amazon Gift Voucher!
              <br />
              <small>Take Our 30-Second Survey</small>
            </button>
          </div>

          {!isRemovalsOrder && (
            <h3>
              <button
                className="submitButton"
                onClick={() =>
                  window.open(
                    'https://www.kitkeeper.co.uk/pre-post-12-2',
                    '_blank',
                  )
                }
              >
                Request Collection Time-Slot
              </button>
            </h3>
          )}
          <div className="review-section">
            <p className="review-message">Leave us a review on Trustpilot</p>
            <TrustPilotReviewCollector />
          </div>
        </div>
      </div>
    </>
  );
}

export default withRouter(SuccessComponent as any);
